import { createServerFn } from "@tanstack/react-start";
import { getRequestIP } from "@tanstack/react-start/server";

export interface MaintenanceStatus {
  active: boolean;
  message: string | null;
}

interface CachedSettings {
  maintenance_mode: boolean | null;
  maintenance_message: string | null;
  allowed_ips: string[] | null;
}

// In-memory cache (per worker isolate). The maintenance config changes very
// rarely, but the root loader runs on EVERY page request and awaits this query
// before SSR can respond — a DB roundtrip on the critical path that dominated
// the document's TTFB (~800ms). Caching the raw settings row for a short TTL
// removes that roundtrip from almost every request while still re-checking the
// per-request client IP against the allowlist (so the cache never leaks a
// maintenance bypass to the wrong visitor).
const SETTINGS_TTL_MS = 60_000;
let settingsCache: { data: CachedSettings | null; at: number } | undefined;

async function fetchSettings(): Promise<CachedSettings | null> {
  const { localAdmin } = await import("@/lib/pending-orders.server");
  const { data, error } = await localAdmin
    .from("site_settings")
    .select("maintenance_mode, maintenance_message, allowed_ips")
    .eq("id", true)
    .maybeSingle();
  if (error) throw error;
  return (data as CachedSettings | null) ?? null;
}

let refreshing: Promise<void> | undefined;

function refreshInBackground() {
  if (refreshing) return;
  refreshing = fetchSettings()
    .then((data) => {
      settingsCache = { data, at: Date.now() };
    })
    .catch(() => {
      // Κρατάμε ό,τι υπάρχει ήδη στην cache· δεν ρισκάρουμε το SSR.
    })
    .finally(() => {
      refreshing = undefined;
    });
}

// TTFB: το ερώτημα στη βάση ΔΕΝ μπαίνει ποτέ στο κρίσιμο μονοπάτι. Αν η cache
// είναι άδεια ή παλιά, απαντάμε αμέσως με ό,τι έχουμε (ή με «χωρίς συντήρηση»)
// και ανανεώνουμε στο παρασκήνιο. Έτσι κάθε SSR απάντηση ξεκινά χωρίς αναμονή
// για roundtrip βάσης (κέρδος ~0.5-1s σε κρύα isolates).
type CacheEntry = { data: CachedSettings | null; at: number };

function readCache(): CacheEntry | undefined {
  return settingsCache;
}

async function loadSettings(): Promise<CachedSettings | null> {
  const cached = readCache();
  if (!cached) {
    // Κρύο isolate: ΠΡΕΠΕΙ να περιμένουμε, αλλιώς η συντήρηση δεν θα έκρυβε
    // το κατάστημα. Γίνεται μία φορά ανά isolate.
    refreshInBackground();
    try {
      await refreshing;
    } catch {
      // αγνοούμε — επιστρέφουμε ό,τι υπάρχει
    }
    return readCache()?.data ?? null;
  }
  if (Date.now() - cached.at >= SETTINGS_TTL_MS) {
    refreshInBackground();
  }
  return cached.data;
}

// Server-side check that decides whether the current visitor should see the
// public "Coming soon" page. Reads the client IP from edge headers and the
// maintenance config from a short-lived in-memory cache backed by the database
// (service-role, RLS bypassed).
//
// Returns active=false on any error so a database hiccup never locks the shop.
export const getMaintenanceStatus = createServerFn({ method: "GET" }).handler(
  async (): Promise<MaintenanceStatus> => {
    try {
      const data = await loadSettings();

      if (!data || !data.maintenance_mode) {
        return { active: false, message: null };
      }

      const ip = getRequestIP({ xForwardedFor: true }) ?? "";
      const allowed: string[] = (data.allowed_ips ?? []).map((v: string) => v.trim());
      if (ip && allowed.includes(ip)) {
        return { active: false, message: null };
      }

      return { active: true, message: data.maintenance_message ?? null };
    } catch (e) {
      console.error("getMaintenanceStatus failed", e);
      return { active: false, message: null };
    }
  },
);